import {
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Renderer2,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

const COLOR_DEFAULT = '#424242';
// Shared app-wide error style (styles.css)
const ERROR_CLASS = 'sr-error-area';

/**
 *   - Replacement for angular2-materialize characterCounter
 *  
  */

@Directive({
  selector: 'input[characterCounter], textarea[characterCounter]',
})
export class CharacterCounterDirective implements OnInit, OnDestroy {
  @Input() length: number | string;
  @Input() maxlength: number | string;
  @Input() warnAtLimit = false;

  private counterEl: HTMLElement | null = null;
  private statusEl: HTMLElement | null = null;
  private unlistenInput: () => void;
  private valueSub: any;
  private max = 0;
  private atLimit = false;

  constructor(
    private el: ElementRef<HTMLInputElement | HTMLTextAreaElement>,
    private renderer: Renderer2,
    private translateService: TranslateService,
    @Optional() private ngControl: NgControl
  ) { }

  ngOnInit(): void {
    this.resolveMax();

    // If no max length → DO NOTHING.
    if (this.max <= 0) return;

    this.createCounter();
    this.updateCounter();

    // Listen to input changes
    this.unlistenInput = this.renderer.listen(
      this.el.nativeElement,
      'input',
      () => this.updateCounter()
    );

    // Listen to reactive form changes
    if (this.ngControl?.valueChanges) {
      this.valueSub = this.ngControl.valueChanges.subscribe(() =>
        this.updateCounter()
      );
    }
  }

  private resolveMax() {
    const len = this.length != null ? Number(this.length) : 0;
    const maxLen = this.maxlength != null ? Number(this.maxlength) : 0;

    this.max = len > 0 ? len : maxLen;
  }

  private createCounter() {
    this.counterEl = this.renderer.createElement('span');
    this.renderer.addClass(this.counterEl, 'character-counter');

    // MaterializeCSS 0.100.2 styling
    this.renderer.setStyle(this.counterEl, 'float', 'right');
    this.renderer.setStyle(this.counterEl, 'font-size', '12px');
    this.renderer.setStyle(this.counterEl, 'color', COLOR_DEFAULT);

    const parent = this.el.nativeElement.parentNode;
    if (parent) {
      this.renderer.appendChild(parent, this.counterEl);
    }

    if (this.warnAtLimit) {
      this.statusEl = this.renderer.createElement('span');
      this.renderer.setAttribute(this.statusEl, 'aria-live', 'polite');
      this.renderer.addClass(this.statusEl, ERROR_CLASS);
      this.renderer.setStyle(this.statusEl, 'display', 'block');
      this.renderer.setStyle(this.statusEl, 'clear', 'both');

      if (parent) {
        this.renderer.appendChild(parent, this.statusEl);
      }
    }
  }

  private updateCounter() {
    if (!this.counterEl) return;

    const value =
      this.ngControl?.value ??
      this.el.nativeElement.value ??
      '';

    const len = String(value).length;
    this.counterEl.textContent = `${len} / ${this.max}`;

    if (this.warnAtLimit) {
      this.atLimit = len >= this.max;
      if (this.atLimit) {
        this.renderer.addClass(this.counterEl, ERROR_CLASS);
        this.renderer.removeStyle(this.counterEl, 'color');
      } else {
        this.renderer.removeClass(this.counterEl, ERROR_CLASS);
        this.renderer.setStyle(this.counterEl, 'color', COLOR_DEFAULT);
      }

      if (this.statusEl) {
        this.statusEl.textContent = this.atLimit
          ? this.translateService.instant('UNIVERSAL-CHARACTER_LIMIT_REACHED')
          : '';
      }
    }
  }

  ngOnDestroy(): void {
    if (this.unlistenInput) this.unlistenInput();
    if (this.valueSub) this.valueSub.unsubscribe();

    if (this.counterEl?.parentNode) {
      this.renderer.removeChild(
        this.counterEl.parentNode,
        this.counterEl
      );
    }

    if (this.statusEl?.parentNode) {
      this.renderer.removeChild(
        this.statusEl.parentNode,
        this.statusEl
      );
    }
  }
}
